import type {PillProps} from 'src/design/atoms/Pill';
import type {DocumentResponse} from 'src/api/ProactApiClient';
import {
  DocumentReviewRequestStateEnum,
  DocumentReviewTypeEnum,
  DocumentStatusEnum,
} from 'src/api/ProactApiClient';
import {formatDate} from 'src/shared/utils/formatters';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

/**
 * Pill config for a document/version's `Status`. Returns `undefined` for
 * `None`/`Approved` — no badge is shown for the steady-state, unreviewed
 * case.
 */
export const DocumentStatusPill = (
  status: DocumentStatusEnum | undefined
): PillProps | undefined => {
  switch (status) {
    case DocumentStatusEnum.PendingReview:
      return {text: 'Pending Approval', color: 'yellow'};
    case DocumentStatusEnum.Rejected:
      return {text: 'Rejected', color: 'red'};
    default:
      return undefined;
  }
};

/** "v<N> under review" chip shown alongside the active version's status. */
export const DocumentPendingVersionPill = (
  document: Pick<DocumentResponse, 'pendingVersion'> | undefined
): PillProps | undefined => {
  if (!document?.pendingVersion) {
    return undefined;
  }
  return {text: `v${document.pendingVersion} under review`, color: 'yellow'};
};

/** Combined pill list for a document row (list/explorer views). */
export const DocumentReviewPillMapping = (
  document: DocumentResponse
): PillProps[] => {
  const pills: PillProps[] = [];
  const statusPill = DocumentStatusPill(document.status);
  if (statusPill) {
    pills.push(statusPill);
  }
  const pendingPill = DocumentPendingVersionPill(document);
  if (pendingPill) {
    pills.push(pendingPill);
  }
  return pills;
};

export const DocumentReviewTypeLabel = (
  type: DocumentReviewTypeEnum
): string => {
  switch (type) {
    case DocumentReviewTypeEnum.Approval:
      return 'Approval';
    case DocumentReviewTypeEnum.Acknowledgment:
      return 'Acknowledgment';
    default:
      return 'Unknown';
  }
};

/** UTC-naive backend timestamp formatted for display in the local timezone. */
export const formatReviewDate = (date: Date | string | undefined): string => {
  if (!date) return '—';
  return formatDate(parseUtcDate(date));
};

export const DocumentReviewRoundStatePill = (
  state: DocumentReviewRequestStateEnum
): PillProps => {
  switch (state) {
    case DocumentReviewRequestStateEnum.Pending:
      return {text: 'Pending', color: 'yellow'};
    case DocumentReviewRequestStateEnum.Approved:
      return {text: 'Approved', color: 'green'};
    case DocumentReviewRequestStateEnum.Rejected:
      return {text: 'Rejected', color: 'red'};
    case DocumentReviewRequestStateEnum.Completed:
      return {text: 'Completed', color: 'green'};
    case DocumentReviewRequestStateEnum.Cancelled:
      return {text: 'Cancelled', color: 'gray'};
    default:
      return {text: 'Unknown', color: 'gray'};
  }
};
